<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import type { OmniSelectProps } from "@/types/props";

const props = withDefaults(defineProps<OmniSelectProps>(), {
  itemTitle: "name",
  itemValue: "id",
  disabled: false,
  required: false,
});

const hasValueChanged = ref<boolean>(false);

const emit = defineEmits(["change", "update:modelValue", "search"]);

const model = computed({
  get: () => props.modelValue,
  set: (value) => {
    emit("update:modelValue", value);
    emit("change", value);
  },
});

watch(model, (newValue, oldValue) => {
  hasValueChanged.value = JSON.stringify(newValue) !== JSON.stringify(oldValue);
});

function onBlur() {
  if (hasValueChanged.value) {
    emit("search", model.value);
    hasValueChanged.value = false;
  }
}
</script>

<template>
  <v-select
    :id="props.id"
    v-model="model"
    :class="{ required: props.required }"
    :clearable="props.clearable"
    :disabled="props.disabled"
    :item-title="props.itemTitle"
    :item-value="props.itemValue"
    :items="props.items"
    :label="props.label"
    :multiple="props.multiple"
    :readonly="props.readonly"
    :rules="props.rules"
    color="primary"
    density="compact"
    hide-details="auto"
    variant="outlined"
    @blur="onBlur"
  >
    <template #item="{ item, props: itemProps }">
      <v-divider v-if="typeof item.raw === 'object' && 'divider' in item.raw" />
      <v-list-subheader v-else-if="typeof item.raw === 'object' && 'header' in item.raw" :title="item.raw.header" />
      <v-list-item v-else :disabled="item.raw.disabled" :label="item.title" :value="item.value" v-bind="itemProps">
        <template v-if="props.multiple" #prepend="status">
          <v-checkbox-btn
            :key="item.value"
            :model-value="status.isSelected"
            :ripple="false"
            color="primary"
            tabindex="-1"
          />
        </template>
        <template v-if="typeof item.raw === 'object' && 'suffix' in item.raw" #append>
          <div class="text-grey-darken-2">{{ item.raw.suffix }}</div>
        </template>
      </v-list-item>
    </template>

    <template v-if="$slots.selection" #selection>
      <slot name="selection"></slot>
    </template>
  </v-select>
</template>

<style lang="scss">
@import "@/assets/style/color";

.v-list-subheader {
  font-size: 11px;
  color: $grey;
}
</style>

<style lang="scss" scoped>
.v-input--readonly {
  pointer-events: none;

  &:deep(.v-field__append-inner) {
    display: none;
  }
}
</style>
